/**
 * One door — `docs/rowted-admin-v2.md` §0.7, §2.6, mockup board 1.
 *
 * Email and password, and then whichever second step the account needs. The
 * page does not know, and must not try to work out, whether an address belongs
 * to a customer's employee or to a Rowted admin: the server answers that, and
 * answers it the same way and in the same time for an address that belongs to
 * neither. All this screen does is draw what came back.
 *
 * Three shapes, in the order somebody meets them:
 *
 *   1. **Credentials.** Email, password, remember this device.
 *   2. **The code**, with the destination named *above* it. Naming it matters:
 *      an admin account and an office account both ask for six digits, and
 *      "Rowted Admin" on a black tile is how somebody notices they are about
 *      to open the wrong one.
 *   3. **The chooser**, for the rare address that is both. Two cards, a radio
 *      each, and a Continue that says where it is going.
 */
import { useEffect, useRef, useState } from 'react'
import { ApiError, type MfaMethod } from '@rowted/shared'
import { request, setCsrfToken } from '../lib/api.js'
import { setAdminCsrfToken } from '../lib/admin-api.js'
import { useHost, type HostTenantInfo } from '../lib/use-host.js'
import { Button } from '../components/primitives.js'
import { MfaPanels } from '../components/mfa-panels.js'
import { assertPasskey } from '../lib/passkey.js'
import { RowtedMark } from '../frame/logo.js'
import { IconNext, IconSecurity } from '../frame/icons.js'
import { cn } from '../lib/cn.js'

/** Where a sign-in is headed. The server names it; this file only draws it. */
interface Destination {
  kind: 'tenant' | 'platform'
  name: string
}

/**
 * One of the two doors this address opens.
 *
 * `ready` is whether the password just typed opened *this* side. The one it
 * did carries a **ticket** and goes straight on; the other carries none, and
 * picking it asks for its own password.
 *
 * **It is a ticket, not a visit**, and that is not pedantry — it is the bug
 * this field had. The 0102 rename swept the client for the word `ticket` and
 * renamed this one with the rest, but an auth ticket is a two-minute
 * single-use credential and has nothing to do with a crew going to a
 * property; `shared/src/api.ts` says so above `chooseRequest` and the server
 * never moved. So `option.visit` was `undefined` on every option the server
 * sent, every pick fell through to "this side's password has not been given
 * yet", and the chooser asked for the same password a second time — on the
 * side it had *just* been proved for.
 */
interface ChooseOption extends Destination {
  ready: boolean
  ticket?: string
}

type SignInResult =
  | { result: 'signed_in'; csrfToken: string; destination?: Destination }
  | {
    result: 'mfa_required'
    methods: MfaMethod[]
    challenge: string
    destination?: Destination
  }
  | { result: 'choose'; options: ChooseOption[] }

/**
 * "Remember this device" — 30 days (`sales_app`'s window, to the minute).
 *
 * Drawn **at both steps that can be the last one**: the password, and the
 * code. It used to be on the password step alone, which is the step that
 * creates a session only for somebody with no second factor — "an account
 * with two-step on never passes through `finishTenantSignIn`". So every
 * Rowted admin, and every office role whose role requires a code, saw the box
 * once, on a screen where it decided nothing visible, and then never again on
 * the screen where the session is actually made. The value already rode along
 * with the code; what was missing was any way to see it or change it there.
 */
function RememberDevice({
  checked, onChange,
}: {
  checked: boolean
  onChange: (next: boolean) => void
}) {
  return (
    <label className="flex cursor-pointer items-start gap-2.5 py-1.5">
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="mt-0.5 h-[17px] w-[17px] accent-accent"
      />
      <span className="flex flex-col gap-px">
        <span className="text-body font-semibold">Remember this device</span>
        <span className="text-cell text-text-muted">
          30 days · not on a shared computer
        </span>
      </span>
    </label>
  )
}

/**
 * Where the app opens once the cookie is set.
 *
 * `?next=` wins, when it is a path on this site — somebody who followed a link
 * into the app and was asked to sign in should land on the thing they clicked.
 * Only a path: an absolute URL here is an open redirect, which is how a
 * convincing phishing link gets built out of a real sign-in page.
 */
function landing(destination: Destination | null): string {
  const next = new URLSearchParams(window.location.search).get('next')
  if (next && next.startsWith('/') && !next.startsWith('//')) return next
  return destination?.kind === 'platform' ? '/admin/clients' : '/'
}

export function SignIn() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [rememberDevice, setRememberDevice] = useState(false)
  const [challenge, setChallenge] = useState<string | null>(null)
  /* Every way this account can answer — §4.4. `MfaPanels` picks which opens. */
  const [methods, setMethods] = useState<MfaMethod[]>([])
  const [destination, setDestination] = useState<Destination | null>(null)
  const [options, setOptions] = useState<ChooseOption[] | null>(null)
  /* By side, not by ticket: the side that needs a password has no ticket. */
  const [picked, setPicked] = useState<Destination['kind'] | null>(null)
  /** Set when the picked side wants its own password. */
  const [asking, setAsking] = useState<ChooseOption | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [linkSent, setLinkSent] = useState(false)
  const [busy, setBusy] = useState(false)

  /** Back to the beginning — "Not you?" and every unrecoverable error. */
  function restart() {
    setChallenge(null)
    setMethods([])
    setDestination(null)
    setOptions(null)
    setPicked(null)
    setAsking(null)
    setPassword('')
  }

  function land(result: Extract<SignInResult, { result: 'signed_in' }>) {
    /*
     * The token goes where the side that issued it will look for it.
     *
     * The two clients keep separate CSRF tokens because they carry separate
     * cookies and separate headers (`lib/admin-api.ts`), and one sign-in page
     * now issues either. Storing an admin's token under the tenant key left
     * every request from Rowted Admin without a header, which the server
     * answered 403 — correctly, and confusingly, since the session itself was
     * fine.
     */
    if (result.destination?.kind === 'platform') setAdminCsrfToken(result.csrfToken)
    else setCsrfToken(result.csrfToken)
    window.location.assign(landing(result.destination ?? null))
  }

  function accept(result: SignInResult) {
    if (result.result === 'choose') {
      setOptions(result.options)
      /* Start on the one that is ready, which is the one they just proved. */
      setPicked((result.options.find((option) => option.ready) ?? result.options[0])?.kind ?? null)
      return
    }
    if (result.result === 'mfa_required') {
      /*
       * The chooser is done with — and clearing it is not housekeeping.
       *
       * The three shapes are drawn by one nested conditional in the order
       * asking → chooser → code → credentials, so a lingering `options` keeps
       * the chooser on the screen *over* the code step. Picking Rowted Admin
       * therefore did nothing visible: the ticket was spent, the challenge was
       * in hand, and the page still showed two cards and a Continue that could
       * only be pressed once. Found in a browser; the API test above could not
       * see it, because from the server's side everything had worked.
       */
      setOptions(null)
      setAsking(null)
      setChallenge(result.challenge)
      setMethods(result.methods ?? [])
      setDestination(result.destination ?? null)
      setPassword('')
      return
    }
    land(result)
  }

  async function run(work: () => Promise<SignInResult>) {
    setError(null)
    setBusy(true)
    try {
      accept(await work())
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Something went wrong. Try again.')
      /* A spent challenge cannot be retried, so a wrong code starts over. */
      if (caught instanceof ApiError && caught.code === 'mfa_invalid') restart()
    } finally {
      setBusy(false)
    }
  }

  /**
   * Landing after a switch — `docs/identity-and-sign-in.md` §2.2.
   *
   * **This is what was missing.** `switchUrl` has built `<host>/switch?t=…`
   * since Identity C and `POST /auth/switch/arrive` has been ready to spend
   * the ticket, and nothing in the client ever called it: `/switch` matched
   * no route, fell through to the catch-all, found no session and redirected
   * here. So every switch ended at a sign-in page, which is exactly what
   * switching exists to avoid.
   *
   * It is a mode of this page rather than a page of its own because the
   * server answers `/switch/arrive` with the same `SignInResult` a sign-in
   * answers: `signed_in`, or `mfa_required` when the target wants its own
   * second step (always, for Rowted Admin). `accept()` already draws both, so
   * the whole of the second case — the panels, *Try another way*, a recovery
   * code — comes for free. A separate page would have been a second copy of
   * all of it, drifting from the first.
   */
  const arriving = useRef(false)
  /* Drawn while the ticket is in flight, cleared the moment anything else is
     true: the code step, or a ticket that had expired and left somebody at an
     ordinary sign-in with no idea why. */
  const [opening, setOpening] = useState(
    () => window.location.pathname === '/switch'
      && new URLSearchParams(window.location.search).has('t'))

  useEffect(() => {
    if (arriving.current) return
    const ticket = new URLSearchParams(window.location.search).get('t')
    if (!ticket || window.location.pathname !== '/switch') return
    arriving.current = true
    /* `ticket`, which is what `/auth/switch/arrive` parses — the same rename
       that broke the chooser above renamed this one too, so every switch
       posted a body the route refused and landed on a sign-in form, which is
       the one thing switching exists to avoid. */
    void run(() => request<SignInResult>('/auth/switch/arrive', {
      method: 'POST', body: { ticket },
    })).finally(() => setOpening(false))
    /* Once, on mount: the ticket is single-use and a second attempt at it is
       a refusal, not a retry — which is what the ref guards, since React 18's
       StrictMode runs an effect twice in development. */
  }, [])

  const submit = (event: React.FormEvent) => {
    event.preventDefault()

    /*
     * The second password. Sent as an ordinary sign-in naming the side, so
     * there is no new way in: the server still checks both passwords and only
     * honours a match on the side that was asked for.
     */
    if (asking) {
      void run(() => request<SignInResult>('/auth/sign-in', {
        method: 'POST', body: { email, password, rememberDevice, side: asking.kind },
      }))
      return
    }

    if (options) {
      const option = options.find((candidate) => candidate.kind === picked)
      if (!option) return
      if (!option.ticket) {
        /* No ticket: this side's password has not been given yet. */
        setAsking(option)
        setPassword('')
        setError(null)
        return
      }
      const { ticket } = option
      void run(() => request<SignInResult>('/auth/choose', {
        method: 'POST', body: { ticket, rememberDevice },
      }))
      return
    }
    /*
     * The panel owns the answer once there is a challenge — it knows which
     * method is showing and a passkey's answer is not a code at all. This
     * branch used to post `method: 'totp'` whatever was on screen, so pressing
     * Enter on an email code would have sent it as an authenticator code and
     * been refused for the wrong reason.
     */
    if (challenge) return
    void run(() => request<SignInResult>('/auth/sign-in', {
      method: 'POST', body: { email, password, rememberDevice },
    }))
  }

  const chosen = asking ?? options?.find((option) => option.kind === picked) ?? null
  const hostState = useHost()
  const here = hostState.state === 'ready' ? hostState.host.tenant : null

  return (
    <div className="flex min-h-app items-center justify-center bg-bg p-4">
      <form
        onSubmit={submit}
        className={cn('flex w-full max-w-[440px] flex-col gap-4 rounded-dialog',
          'border border-line bg-surface p-7 shadow-popover')}
      >
        {/*
          * On a company's own address this is the company's head, not
          * Rowted's — §5.3, board 5 frame 1. Somebody who is about to type a
          * password should be able to see, above the field, whose door they
          * are at; "rowted" over every sign-in page is what made a
          * look-alike page indistinguishable from the real one.
          */}
        {here ? (
          <HostHead tenant={here} />
        ) : (
          <span className="flex items-center gap-2">
            <RowtedMark size={30} />
            <b className="text-[17px] tracking-[-0.02em] text-text">rowted</b>
          </span>
        )}

        <div className="flex flex-col gap-1">
          <b className="text-[20px] tracking-[-0.01em] text-text">
            {opening
              ? 'One moment'
              : asking
                ? 'One more password'
                : options ? 'Where to?' : challenge ? 'Enter your code' : 'Sign in'}
          </b>
          <span className="text-cell leading-[1.45] text-text-muted">
            {asking
              ? <>The password you just typed opens your other account. Type the one
                  for <b className="text-text">{asking.name}</b>.</>
              : options
                ? <><b className="text-text">{email}</b>{' '}belongs to two accounts. Pick one —
                    you can switch from the account menu later.</>
                : challenge
                  ? 'From your authenticator app. Admin accounts always ask.'
                  /*
                   * Two different promises, because two different doors.
                   *
                   * On the front door one sign-in really does open either side,
                   * and saying so is the point of §2.6. At a company's own
                   * address it does not: `findAccount` is scoped to this host,
                   * so a Rowted admin's password is refused here. Leaving the
                   * front door's sentence up was telling somebody the door
                   * does something it is built not to do — found by reading
                   * the page at keystone.localhost, not by any test.
                   */
                  : here
                    /*
                     * Board 5's own words, and they are better than mine were:
                     * "Only X accounts sign in here" states the narrowing
                     * exactly — `findAccount` is scoped to this host, so an
                     * admin's password and another client's are both refused.
                     * The way out sits in the same sentence rather than at the
                     * foot of the form, where somebody at the wrong address
                     * would have to read past the fields to find it.
                     */
                    ? <>Only <b className="text-text">{here.displayName}</b> accounts sign
                      in here. Somewhere else?{' '}
                      <a
                        href={hostState.state === 'ready' ? hostState.host.rootUrl : '/'}
                        className="font-semibold text-accent hover:text-accent-dark"
                      >
                        {hostState.state === 'ready' ? hostState.host.rootHost : 'rowted'}
                      </a></>
                    : 'One sign-in for your company’s app and for Rowted Admin — '
                      + 'we take you to the right place.'}
          </span>
        </div>

        {/* Nothing to fill in while a switch ticket is being spent — the
            answer either lands somewhere else or turns into the code step. */}
        {opening ? null : asking ? (
          <>
            <DestinationRow
              destination={asking}
              email={email}
              onBack={() => { setAsking(null); setPassword(''); setError(null) }}
              backLabel="Pick again"
            />
            <Field label="Password" required>
              <input
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="current-password"
                autoFocus
                required
                className={cn('h-9 w-full rounded-control border border-line bg-surface px-[11px]',
                  'text-body font-semibold text-text')}
              />
            </Field>
          </>
        ) : options ? (
          <div className="flex flex-col gap-2">
            {options.map((option) => (
              <label
                key={option.kind}
                className={cn('flex cursor-pointer items-center gap-3 rounded-popup border',
                  'px-3.5 py-3',
                  option.kind === picked
                    ? 'border-accent-line bg-accent-soft'
                    : 'border-line bg-surface hover:bg-hover')}
              >
                <DestinationTile destination={option} size={36} />
                <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <b className="truncate text-body">{option.name}</b>
                  <span className="text-cell text-text-muted">
                    {/*
                      * What picking this one will do. A side the password did
                      * not open is not refused and not greyed — it asks for its
                      * own password, which is the ordinary case when the two
                      * accounts are kept apart properly.
                      */}
                    {option.ready
                      ? (option.kind === 'platform' ? 'Rowted admin · asks for a code' : 'Office')
                      : 'Asks for its own password'}
                  </span>
                </span>
                <input
                  type="radio"
                  name="destination"
                  checked={option.kind === picked}
                  onChange={() => setPicked(option.kind)}
                  className="h-[18px] w-[18px] accent-accent"
                />
              </label>
            ))}
          </div>
        ) : challenge ? (
          <>
            {destination && (
              <DestinationRow destination={destination} email={email} onBack={restart} />
            )}
            {/*
              * One set of panels, three places (§4.4): here, in the step-up
              * before a sensitive action, and on arrival after a switch. The
              * page used to draw six boxes and nothing else, which could only
              * ever ask for an authenticator code.
              */}
            <MfaPanels
              methods={methods}
              busy={busy}
              onSend={async (kind) => {
                const sent = await request<{ resendAfter?: number }>('/auth/mfa/send', {
                  method: 'POST', body: { challenge, method: kind },
                })
                return sent.resendAfter ?? 45
              }}
              onPasskey={() => assertPasskey(challenge)}
              onAnswer={(answer) => {
                /*
                 * The box travels with the code, not only with the password.
                 * The session that actually opens is the one this request
                 * makes, so sending it only at the first step meant the
                 * checkbox did nothing for anybody with two-step on — which
                 * is every Rowted admin.
                 */
                void run(() => request<SignInResult>('/auth/mfa/verify', {
                  method: 'POST', body: { challenge, rememberDevice, ...answer },
                }))
              }}
            />
            {/*
              * And the box itself, because this is the request that makes the
              * session. Carrying the value from the password step was half of
              * it; the other half is being able to see what it is set to at
              * the moment it takes effect, and to change your mind.
              */}
            <RememberDevice checked={rememberDevice} onChange={setRememberDevice} />
          </>
        ) : (
          <>
            <Field label="Email" required>
              <input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                autoComplete="username"
                autoFocus
                required
                className={cn('h-9 w-full rounded-control border border-line bg-surface px-[11px]',
                  'text-body font-semibold text-text')}
              />
            </Field>
            <Field label="Password" required>
              <input
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="current-password"
                required
                className={cn('h-9 w-full rounded-control border border-line bg-surface px-[11px]',
                  'text-body font-semibold text-text')}
              />
            </Field>
            <RememberDevice checked={rememberDevice} onChange={setRememberDevice} />
          </>
        )}

        {error && (
          <p role="alert"
            className="m-0 rounded-button bg-danger-bg px-3 py-2 text-cell text-danger">
            {error}
          </p>
        )}

        {/*
          * Not during a challenge: `MfaPanels` carries its own Continue, whose
          * label and whose enabling depend on which method is showing — a
          * passkey has nothing to type and a recovery code is ten characters.
          * Two Continues on one screen is worse than one in the right place.
          */}
        {!challenge && !opening && (
        <Button
          type="submit"
          variant="primary"
          size="big"
          disabled={busy || (Boolean(asking) && password.length === 0)}
          className="w-full justify-center"
        >
          <IconNext size={15} />
          {busy
            ? 'Checking…'
            : chosen
              ? `Continue to ${chosen.name}`
              : 'Continue'}
        </Button>
        )}

        {/*
          * The other way in, for somebody who cannot remember a password.
          *
          * The mockup draws a "Forgot?" beside the password label as well.
          * There is no password-reset endpoint that issues its own token yet —
          * `set-password` consumes one, and only an invite makes one — so
          * drawing a second control that does nothing would be worse than
          * drawing one. The link below is the real recovery path.
          */}
        {!challenge && !options && (
          <div className="flex justify-center gap-1.5 text-cell text-text-muted">
            {linkSent ? (
              <span>If that address has an account, a sign-in link is on its way.</span>
            ) : (
              <>
                Or
                <button
                  type="button"
                  disabled={busy || !email.trim()}
                  onClick={() => {
                    setBusy(true)
                    void request('/auth/magic-link', { method: 'POST', body: { email } })
                      .then(() => setLinkSent(true))
                      .catch(() => setLinkSent(true))
                      .finally(() => setBusy(false))
                  }}
                  className={cn('font-semibold text-accent hover:text-accent-dark',
                    'disabled:text-text-faint')}
                >
                  email me a sign-in link
                </button>
              </>
            )}
          </div>
        )}
      </form>
    </div>
  )
}

/**
 * The company's own head, on the company's own address — §5.3, board 5 frame 1.
 *
 * Three things, and each earns its place. **The mark and the name**, so the
 * page is visibly theirs. **The verified chip**, which says the address in the
 * bar out loud: the one thing a look-alike page cannot copy is the host, so
 * printing it beside a tick is the difference between "this looks like
 * Keystone" and "this is at keystone.app.rowted.com". And **Somewhere else?**,
 * because the commonest way to arrive at the wrong company's door is to
 * bookmark a colleague's.
 *
 * The brand colour tints the tile rather than the buttons. A sign-in page in
 * somebody's own green is a nice thought and a bad idea: the primary button is
 * how the design system says "this is the action", and a company whose colour
 * is a pale yellow would get an unreadable one.
 */
/** `WO` from `Woolley Outdoors` — two words at most, as the mockup draws it. */
function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean)
  if (words.length === 0) return '?'
  return (words.length === 1
    ? words[0]!.slice(0, 2)
    : `${words[0]![0]}${words[1]![0]}`).toUpperCase()
}

function HostHead({ tenant }: { tenant: HostTenantInfo }) {
  return (
    <div className="flex flex-col gap-3">
      <span className="flex items-center gap-2.5">
        {tenant.hasMark ? (
          <img
            src="/api/v1/host/mark"
            alt=""
            className="size-9 shrink-0 rounded-full object-contain"
          />
        ) : (
          /*
            * Initials in the accent tint, not the company's own colour on a
            * filled tile. A brand colour is whatever a customer typed, and a
            * pale one would put white text on white — the tint is the same
            * treatment `Avatar` gives a person for the same reason.
            */
          <span
            className={cn('flex size-9 shrink-0 items-center justify-center rounded-full',
              'bg-accent-soft text-[12px] font-bold tracking-[0.02em] text-accent')}
          >
            {initialsOf(tenant.displayName)}
          </span>
        )}
        <span className="flex min-w-0 flex-col">
          <b className="truncate text-[15px] tracking-[-0.01em] text-text">
            {tenant.displayName}
          </b>
          {/* Board 5: the company is the head, and Rowted is the small print. */}
          <span className="text-[11.5px] text-text-muted">on rowted</span>
        </span>
      </span>
      {/*
        * One pill, carrying the host. The host is the one thing a look-alike
        * page cannot copy, so it belongs *inside* the thing that says
        * "verified" rather than beside it in grey.
        */}
      <span
        className={cn('inline-flex h-6 w-fit max-w-full items-center gap-1.5 rounded-full',
          'bg-accent-soft px-[9px] text-[11.5px] font-semibold text-accent-dark')}
      >
        <IconSecurity size={12} />
        <span className="truncate">Verified environment · {window.location.host}</span>
      </span>
    </div>
  )
}

/**
 * Who this step is for, above the step itself.
 *
 * The same row serves the code step and the second password: a tile, the name,
 * the address, and one way back out. Naming the destination is the whole
 * safeguard — two accounts on one address ask for the same six digits, and
 * this is how somebody notices which one they are opening.
 */
function DestinationRow({ destination, email, onBack, backLabel = 'Not you?' }: {
  destination: Destination; email: string; onBack: () => void; backLabel?: string
}) {
  return (
    <div className={cn('flex items-center gap-2.5 rounded-field border border-line',
      'bg-field px-3 py-2.5')}
    >
      <DestinationTile destination={destination} size={32} />
      <span className="flex min-w-0 flex-col gap-px">
        <span className="flex items-center gap-1.5">
          <b className="truncate text-[13px]">{destination.name}</b>
          {destination.kind === 'platform' && <AdminTag />}
        </span>
        <span className="truncate text-sub text-text-muted">{email}</span>
      </span>
      <button
        type="button"
        onClick={onBack}
        className="ml-auto shrink-0 text-cell font-semibold text-accent hover:text-accent-dark"
      >
        {backLabel}
      </button>
    </div>
  )
}

/**
 * The black shield for Rowted Admin, the tenant's initials for a company.
 *
 * Black is the one colour the app never uses for a tenant, which is the point:
 * the tile is the thing somebody sees before they read anything.
 */
function DestinationTile({ destination, size }: { destination: Destination; size: number }) {
  if (destination.kind === 'platform') {
    return (
      <span
        aria-hidden
        style={{ width: size, height: size }}
        className="inline-flex shrink-0 items-center justify-center rounded-tile bg-text text-bg"
      >
        <IconSecurity size={Math.round(size / 2)} />
      </span>
    )
  }
  const initials = destination.name.split(/\s+/).slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? '').join('')
  return (
    <span
      aria-hidden
      style={{ width: size, height: size }}
      className={cn('inline-flex shrink-0 items-center justify-center rounded-chip',
        'bg-accent-soft text-[12px] font-bold tracking-[0.02em] text-accent')}
    >
      {initials}
    </span>
  )
}

export function AdminTag() {
  return (
    <span className={cn('inline-flex h-5 shrink-0 items-center rounded-button bg-text px-[7px]',
      'text-tag font-bold uppercase tracking-[0.04em] text-bg')}
    >
      Admin
    </span>
  )
}

function Field({ label, required, children }: {
  label: string; required?: boolean; children: React.ReactNode
}) {
  return (
    <label className="flex min-w-0 flex-col gap-1.5">
      <span className="flex items-center text-sub text-text-muted">
        {label}
        {required && (
          <span aria-hidden
            className="ml-1.5 inline-block h-[5px] w-[5px] translate-y-[-1px] rounded-chip bg-accent" />
        )}
      </span>
      {children}
    </label>
  )
}
